import Head from 'next/head';
import Script from 'next/script';
import { useState } from 'react';
import { JSX } from 'react';

import { ContentContainer, RichText, Spinner, Text } from '@ahc-digital/component-library';
import {
  GetServerSideComponentProps,
  RichText as JSSRichText,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { GetSearchEngineName, GetSearchSources } from 'src/shared/SearchUI.service';
import { SwitchToPreviewMessage } from 'src/shared/SwitchToPreviewMessage';
import { translationClasses } from 'src/shared/TranslationClasses';
import { CreateS04SiteSearchModel } from './S04SiteSearch.service';
import { S04SiteSearchProps } from './S04SiteSearch.types';
import { PromptForErrorMessage } from 'src/shared/PromptForErrorMessage';
import { useCustomSitecoreContext } from 'src/shared/useCustomSitecoreContext';
import { useCreateMarketingRenderingHame } from 'src/shared/useCreateMarketingRenderingName';
import { checkDatasourceForRendering } from '@/shared/checkDatasourceForRendering';

const Default = (props: S04SiteSearchProps): JSX.Element => {
  const { engine, configSrc, cssSrc, uiSrc } = props || {};
  const { sitecoreContext } = useSitecoreContext();
  const context = useCustomSitecoreContext();
  const disableElasticSearch = context?.disableElasticSearch?.value;
  const isEditing = sitecoreContext?.pageEditing ?? false;
  const marketingRenderingName = useCreateMarketingRenderingHame(
    props?.rendering?.componentName as string
  );

  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [isUiLoading, setIsUiLoading] = useState(true);

  const model = CreateS04SiteSearchModel(props);

  const renderingName = 'S04 - Site Search';

  if (!model.site) {
    return <PromptForErrorMessage componentName={renderingName} message="Site name not found" />;
  }

  if (!engine) {
    return (
      <PromptForErrorMessage componentName={renderingName} message="Search engine not found" />
    );
  }

  if (isEditing) {
    return (
      <SwitchToPreviewMessage componentName={marketingRenderingName}>
        <ContentContainer
          spacing="40-24"
          supportCopyAlignment="left"
          supportCopy={
            <>
              <h4>Configuration Details:</h4>
              <p>
                <b>Site:</b> {model.site} <br />
                <b>Engine:</b> {engine} <br />
              </p>
            </>
          }
        >
          <Text tag="h4">Editable Fields:</Text>
          <br />
          <Text tag="p" fontStyle="smParagraphBold">
            No results text:
          </Text>
          <RichText>
            <JSSRichText field={props?.fields?.NoResultsText} />
          </RichText>
          <br />
          <Text tag="p" fontStyle="smParagraphBold">
            Scope search to section:
          </Text>
          <Text tag="p">
            Click the pencil icon to select a site section to narrow results down.
          </Text>
        </ContentContainer>
      </SwitchToPreviewMessage>
    );
  }

  if (disableElasticSearch) {
    return <></>;
  }

  return (
    <>
      <Head>
        <link rel="stylesheet" key="search-solution-styles" href={cssSrc} />
      </Head>
      <Script
        src={configSrc}
        strategy="afterInteractive"
        onReady={() => {
          setIsConfigLoading(false);
        }}
      />
      {!isConfigLoading && (
        <Script
          type="module"
          src={uiSrc}
          strategy="afterInteractive"
          onReady={() => {
            window.AHC_ENV?.bindComponents?.();
            setIsUiLoading(false);
          }}
        />
      )}
      {isUiLoading && (
        <ContentContainer>
          <Spinner />
        </ContentContainer>
      )}
      <div
        data-react-component="SiteSearch"
        data-site={model.site}
        data-engine={engine}
        data-model={JSON.stringify(model.dataModel)}
        data-section={JSON.stringify(model.section)}
        className={translationClasses(props?.params)}
        id={props?.params?.AnchorLinkTargetName}
        data-component={props.rendering?.componentName}
      />
    </>
  );
};

export default checkDatasourceForRendering<S04SiteSearchProps>(Default);

export const getServerSideProps: GetServerSideComponentProps = async (_rendering, layoutData) => {
  const context = layoutData.sitecore.context;
  const site = context?.site?.name;
  const engine = GetSearchEngineName(site);
  const { config: configSrc, css: cssSrc, ui: uiSrc } = GetSearchSources();
  return {
    engine,
    configSrc,
    cssSrc,
    uiSrc,
  };
};
